import TextField from "@material-ui/core/TextField";
import PasswordRequirement from "components/PasswordRequirement";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { useLocalization } from "contexts/LocalizationContext";
import { routes } from "pages/routes";
import { ChangeEvent, FocusEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { signUp, type SignUpParams } from "services/users/auth";
import { getErrorsString } from "services/util";
import { PasswordInput } from "../../../../components/PasswordInput/PasswordInput";
import useAccountForm from "../../../SignIn/utils";

import assert from "utils/assert";
import styles from "./EmailSignUp.module.scss";
import { trackEvent } from "services/productAnalytics";
import { PurchaseStudentType } from "services/pricingModel/model";

type Props = {
  studentType: PurchaseStudentType;
  pricingModelCode?: string;
  onSignUp: (email: string, resend_verification_token: string) => void;
};

const initData = {
  firstName: "",
  email: "",
  password: "",
  confirmPassword: "",
};

export default function EmailSignUp({ pricingModelCode, onSignUp, studentType }: Props) {
  const {
    account,
    setAccount,
    errors,
    touched,
    passwordStatus,
    setTouched,
    onValidate,
    onTouched,
  } = useAccountForm({
    ...initData,
    ...(pricingModelCode && {
      lastName: "",
    }),
  });
  const [loading, setLoading] = useState(false);
  const [createErrors, setCreateErrors] = useState("");
  const { t } = useTranslation();

  const { language } = useLocalization();

  const isCreateDisabled = Object.keys(errors).length > 0;

  const handleChangeAccountData = (e: ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleCreate = async (evt: any) => {
    evt.preventDefault();

    const isValidated = onValidate();
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      countryCode: true,
      primaryLanguage: true,
      password: true,
      confirmPassword: true,
      location: true,
    });

    if (isValidated) {
      setLoading(true);

      try {
        const { email, password, firstName } = account;
        assert(email && password && firstName && language, "Missing required fields");
        const address = account.location?.address;
        const profile =
          address?.state !== undefined || address?.street_1 !== undefined
            ? {
                state: address?.state,
                street_1: address?.street_1,
              }
            : undefined;

        const payload: SignUpParams = {
          email,
          passwd: password,
          first_name: firstName,
          last_name: account.lastName,
          city: address?.city,
          country: address?.country,
          timezone: account.location?.timezoneId,
          native_language: account.primaryLanguage ?? undefined,
          profile,
          localization_lang: language,
          student_type: studentType,
          pricing_model_code: pricingModelCode,
        };

        trackEvent("email_sign_up_submitted", { pricingModelCode });

        const resp = await signUp(payload);

        if (resp.sso?.redirect_url) {
          window.location.replace(resp.sso.redirect_url);
        } else if (account.email) {
          onSignUp(account.email, resp.resend_verification_token);
          setLoading(false);
          trackEvent("email_sign_up_success");
        }
      } catch (error: any) {
        setLoading(false);
        setCreateErrors(error);
        trackEvent("sign_up_error", { error });
      }
    }
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const getCreateErrorComponents = (error: string) => {
    let errorString = getErrorsString(error);
    let isLoginLink = errorString.includes("Log in here");

    if (isLoginLink) {
      errorString = errorString.replace("Log in here.", "");
    }

    return (
      <div className={styles.error}>
        {errorString}
        {isLoginLink && (
          <>
            <br />
            <Link to={routes.public.signIn.path()}>{t("Signup.Log in here.")}</Link>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <h2 className={styles.title}>{t("Signup.CreateAccount.emailSignup.headline")}</h2>

      <form onSubmit={handleCreate} className={styles.form}>
        {createErrors && getCreateErrorComponents(createErrors)}

        <div className={styles.formWrapper}>
          <TextField
            className={styles.textField}
            name="firstName"
            label={t("Signup.First Name")}
            variant="outlined"
            value={account.firstName}
            error={!!(errors.firstName && touched.firstName)}
            helperText={touched.firstName && errors.firstName}
            onBlur={handleBlurField}
            onChange={handleChangeAccountData}
          />
          {pricingModelCode && (
            <TextField
              className={styles.textField}
              name="lastName"
              label={t("Signup.Last Name")}
              variant="outlined"
              value={account.lastName}
              error={!!(errors.lastName && touched.lastName)}
              helperText={touched.lastName && errors.lastName}
              onBlur={handleBlurField}
              onChange={handleChangeAccountData}
            />
          )}
          <TextField
            className={styles.textField}
            name="email"
            label={t("Signup.Email Address")}
            variant="outlined"
            value={account.email}
            error={!!(errors.email && touched.email)}
            helperText={touched.email && errors.email}
            onBlur={handleBlurField}
            onChange={handleChangeAccountData}
          />
          <PasswordInput
            className={styles.textField}
            name="password"
            label={t("Signup.Password")}
            variant="outlined"
            autoComplete="new-password"
            type="password"
            value={account.password}
            error={!!(errors.password && touched.password)}
            helperText={touched.password && errors.password}
            onBlur={handleBlurField}
            onChange={handleChangeAccountData}
          />
          <PasswordInput
            className={styles.textField}
            name="confirmPassword"
            label={t("Signup.Confirm Password")}
            variant="outlined"
            autoComplete="new-password"
            type="password"
            value={account.confirmPassword}
            error={!!(errors.confirmPassword && touched.confirmPassword)}
            helperText={touched.confirmPassword && errors.confirmPassword}
            onBlur={handleBlurField}
            onChange={handleChangeAccountData}
          />
        </div>

        {account.password && (
          <div className={styles.passwordConfirmPanel}>
            <PasswordRequirement passwordStatus={passwordStatus} />
          </div>
        )}

        <div className={styles.stepAction}>
          {!loading ? (
            <button disabled={isCreateDisabled} className={styles.btn} type="submit">
              {t("Signup.Create Account")}
            </button>
          ) : (
            <TalaeraSpinner />
          )}
        </div>
      </form>
    </>
  );
}
